/**
 * API 金鑰的**表單草稿、到期換算、編輯差異與顯示用字**（批147）——純函式。
 * 勾選邏輯在 `lib/apiKeyScopes.ts`。
 *
 * ## 到期
 *
 * 後端：`expires_at` 省略或 null＝**無期限**（主人裁示）；有填必須是未來。
 * UI 只讓人選「日期」，送出時補成**當天 23:59:59 的本地時間**再轉 ISO——
 * 選「10/31」的人心裡想的是「10/31 這天還能用」，送 00:00 會少一整天。
 *
 * ## 編輯只送有改的欄位
 *
 * 後端 `PUT` 只改有帶的欄位。前端也只送真的改過的：
 * - 名稱：trim 後與原值不同才送。
 * - scope：**集合**不同才送（順序不算）；送的時候才補寫→讀（`normalizeScopes`）。
 * - 到期：以「本地日期」比對。原值不是 23:59:59（例如舊資料）而使用者沒動日期時
 *   **不送**，保留原本的精確時刻——否則只是改個名稱，到期時間就被悄悄挪到當天晚上。
 */
import type { ApiKeyRow, ApiKeyScopeCatalog, ApiKeyStatus } from '@/api/ls'
import { normalizeScopes, sameScopeSet } from '@/lib/apiKeyScopes'

export type ExpiryMode = 'never' | 'date'

export interface ApiKeyDraft {
  name: string
  scopes: string[]
  expiry: ExpiryMode
  /** 本地日期 `YYYY-MM-DD`；`expiry==='never'` 時保留（切回來還在），送出時忽略。 */
  date: string
}

/** 名稱上限（與後端 `Field(max_length=120)` 同值）。 */
export const API_KEY_NAME_MAX = 120

/** 從「無期限」切到「指定日期」而日期還空著時，預填的天數。 */
export const DEFAULT_EXPIRY_DAYS = 90

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const p2 = (n: number) => String(n).padStart(2, '0')

/** 毫秒時刻 → 本地 `YYYY-MM-DD`。 */
export function localDateOf(ms: number): string {
  const d = new Date(ms)
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`
}

/** ISO 時間 → 本地 `YYYY-MM-DD`（解析不出來回空字串）。 */
export function localDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const t = Date.parse(iso)
  return Number.isNaN(t) ? '' : localDateOf(t)
}

/** 今天（本地）——日期欄的 `min`。 */
export function todayLocal(now: number = Date.now()): string {
  return localDateOf(now)
}

/** 今天起算 N 天後（本地日期）。 */
export function daysFromToday(days: number, now: number = Date.now()): string {
  const d = new Date(now)
  d.setDate(d.getDate() + days)
  return localDateOf(d.getTime())
}

/**
 * 本地日期 → 那一天 **23:59:59（本地）** 的 ISO（UTC，`…Z`）。
 * 格式不對或日期不存在（`2026-02-30`）回 null。
 */
export function expiryIso(date: string): string | null {
  const m = DATE_RE.exec(date.trim())
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  const dt = new Date(y, mo - 1, d, 23, 59, 59, 0)
  // `new Date` 會把 2/30 進位成 3/2——拿回來比對，對不上就是不存在的日期。
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null
  return dt.toISOString()
}

export function emptyDraft(): ApiKeyDraft {
  return { name: '', scopes: [], expiry: 'never', date: '' }
}

/** 編輯：從既有金鑰帶入。 */
export function draftFromKey(key: ApiKeyRow): ApiKeyDraft {
  return {
    name: key.name,
    scopes: [...key.scopes],
    expiry: key.expires_at ? 'date' : 'never',
    date: localDate(key.expires_at),
  }
}

/** 建立表單有沒有輸入過東西（有的話點背景不關閉）。 */
export function isCreateDirty(draft: ApiKeyDraft): boolean {
  return draft.name.trim() !== '' || draft.scopes.length > 0 || draft.expiry === 'date'
}

/** 到期有沒有被改過（以本地日期比對，見檔頭）。 */
export function expiryChanged(draft: ApiKeyDraft, key: Pick<ApiKeyRow, 'expires_at'>): boolean {
  if (!key.expires_at) return draft.expiry === 'date'
  if (draft.expiry === 'never') return true
  return draft.date !== localDate(key.expires_at)
}

/**
 * 表單哪裡不對（null＝可以送）。
 * `base` 給了（編輯）且到期沒動 → 不檢查到期：已過期的金鑰改個名稱不該被「到期日必須在未來」擋下。
 */
export function draftProblem(
  draft: ApiKeyDraft,
  catalog: ApiKeyScopeCatalog | null,
  opts: { base?: Pick<ApiKeyRow, 'expires_at'>; now?: number } = {},
): string | null {
  const name = draft.name.trim()
  if (!name) return '請填寫名稱。'
  if (name.length > API_KEY_NAME_MAX) return `名稱最多 ${API_KEY_NAME_MAX} 字。`
  const scopes = catalog ? normalizeScopes(draft.scopes, catalog) : draft.scopes
  if (!scopes.length) return '至少要勾一個權限範圍。'
  const checkExpiry = !opts.base || expiryChanged(draft, opts.base)
  if (checkExpiry && draft.expiry === 'date') {
    if (!draft.date) return '請選擇到期日，或改回「無期限」。'
    const iso = expiryIso(draft.date)
    if (!iso) return '到期日格式不正確。'
    if (Date.parse(iso) <= (opts.now ?? Date.now())) return '到期日必須是今天或之後。'
  }
  return null
}

/** 建立的 body（`expires_at: null`＝無期限）。 */
export function createBody(
  draft: ApiKeyDraft,
  catalog: ApiKeyScopeCatalog,
): { name: string; scopes: string[]; expires_at: string | null } {
  return {
    name: draft.name.trim(),
    scopes: normalizeScopes(draft.scopes, catalog),
    expires_at: draft.expiry === 'date' ? expiryIso(draft.date) : null,
  }
}

/** 編輯的 body：**只放有改的欄位**（空物件＝沒改，不必送）。 */
export function updateBody(
  draft: ApiKeyDraft,
  key: Pick<ApiKeyRow, 'name' | 'scopes' | 'expires_at'>,
  catalog: ApiKeyScopeCatalog,
): { name?: string; scopes?: string[]; expires_at?: string | null } {
  const body: { name?: string; scopes?: string[]; expires_at?: string | null } = {}
  const name = draft.name.trim()
  if (name !== key.name) body.name = name
  if (!sameScopeSet(draft.scopes, key.scopes)) body.scopes = normalizeScopes(draft.scopes, catalog)
  if (expiryChanged(draft, key)) {
    body.expires_at = draft.expiry === 'never' ? null : expiryIso(draft.date)
  }
  return body
}

// ---------------------------------------------------------------------------
// 顯示用字
// ---------------------------------------------------------------------------

export const API_KEY_STATUS_LABELS: Record<ApiKeyStatus, string> = {
  active: '有效',
  revoked: '已撤銷',
  expired: '已過期',
}

/** 與後端 `services/api_keys.py::REVOKE_REASON_LABELS` 同一組字。 */
export const REVOKE_REASON_LABELS: Record<string, string> = {
  manual: '手動撤銷',
  owner_inactive: '擁有者帳號已停用',
  owner_demoted: '擁有者已不是超級管理者',
  owner_force_logout: '擁有者被強制登出',
  owner_deleted: '擁有者帳號已刪除',
}

/** 撤銷原因的中文（沒見過的值原樣顯示，不吞掉）。 */
export function revokeReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '—'
  return REVOKE_REASON_LABELS[reason] ?? reason
}

/**
 * 存取紀錄的時間：本地 `MM-DD HH:mm:ss`（不是今年才補年份）。
 * 比 `fmtSeen` 多到秒——腳本常在同一分鐘內連打好幾支，只到分鐘的話整頁時間都一樣。
 */
export function fmtLogTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const d = new Date(t)
  const s = `${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`
  return d.getFullYear() === new Date(now).getFullYear() ? s : `${d.getFullYear()}-${s}`
}

/** 存取紀錄狀態碼的色調：2xx／3xx＝ok、4xx＝warn（被擋／限速）、5xx＝bad。 */
export function statusTone(code: number): 'ok' | 'warn' | 'bad' {
  if (code >= 500) return 'bad'
  if (code >= 400) return 'warn'
  return 'ok'
}
